import {
  generateCreditObjectsQueryKey,
  generateFilesQueryKey,
  invalidateQueryIfNotMutating,
} from 'dataLayer/queryKeys';
import { errorHandler } from 'helpers/utils';
import { ChangeEvent } from 'react';
import { useQueryClient, useMutation } from 'react-query';
import { CreditObject, CreditObjectProperties, S3File } from 'types';

export const useCreditObjectMutation = (
  updateCreditObjectHandler: (
    creditProperties: Partial<CreditObjectProperties>,
  ) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateCreditObjectsQueryKey(dealId);

  return useMutation<
    void,
    unknown,
    Partial<CreditObjectProperties>,
    { previousCreditObjects: CreditObject[] | undefined }
  >(updateCreditObjectHandler, {
    mutationKey,
    onMutate: (newProperties) => {
      const previousCreditObjects: CreditObject[] | undefined = qc.getQueryData(
        generateCreditObjectsQueryKey(dealId),
      );
      qc.setQueryData(
        generateCreditObjectsQueryKey(dealId),
        (oldCreditObjects: CreditObject[] | undefined) => {
          if (oldCreditObjects) {
            const creditObjectToOverwrite = oldCreditObjects?.find(
              (creditObj) => creditObj.properties.name === newProperties.name,
            );
            if (creditObjectToOverwrite) {
              creditObjectToOverwrite.properties = {
                ...creditObjectToOverwrite.properties,
                ...newProperties,
              };
            }
          }
          return oldCreditObjects ?? [];
        },
      );
      return { previousCreditObjects };
    },
    onError: (err, _event, context) => {
      errorHandler(err);
      qc.setQueryData(
        generateCreditObjectsQueryKey(dealId),
        context?.previousCreditObjects,
      );
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};

export const useCreateCreditObjectMutation = (
  createCreditObjectHandler: (
    creditProperties: Partial<CreditObjectProperties>,
  ) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateCreditObjectsQueryKey(dealId);

  return useMutation<void, unknown, Partial<CreditObjectProperties>>(
    createCreditObjectHandler,
    {
      mutationKey,
      onMutate: (creditProperties) => {
        const previousCreditObjects: CreditObject[] | undefined =
          qc.getQueryData(generateCreditObjectsQueryKey(dealId));

        const newCreditObject: CreditObject = {
          id: 'optimisticId',
          createdAt: 'optimisticCreatedAt',
          updatedAt: 'optimisticUpdatedAt',
          archived: false,
          properties: {
            name: creditProperties?.name ?? 'optimisticName',
            hubspotCreateDate: 'optimisticHubspotCreateDate',
            hubspotLastModifiedDate: 'optimisticHubspotLastModifiedDate',
            hubspotObjectId: 'optimisticObjectId',
          },
        };

        qc.setQueryData(
          generateCreditObjectsQueryKey(dealId),
          (oldCreditObjects: CreditObject[] | undefined) => [
            ...(oldCreditObjects ?? []),
            newCreditObject,
          ],
        );

        return { previousCreditObjects };
      },
      onError: (err) => {
        errorHandler(err);
      },
      onSettled: () => {
        invalidateQueryIfNotMutating(qc, mutationKey);
      },
    },
  );
};

export const useDeleteCreditObjectMutation = (
  deleteCreditObjectHandler: (creditId: string) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateCreditObjectsQueryKey(dealId);

  return useMutation<void, unknown, string>(deleteCreditObjectHandler, {
    mutationKey,
    onMutate: (creditId) => {
      const previousCreditObjects: CreditObject[] | undefined = qc.getQueryData(
        generateCreditObjectsQueryKey(dealId),
      );
      qc.setQueryData(
        generateCreditObjectsQueryKey(dealId),
        (oldCreditObjects: CreditObject[] | undefined) =>
          oldCreditObjects?.filter((creditObj) => creditObj.id !== creditId) ??
          [],
      );
      return { previousCreditObjects };
    },
    onError: (err) => {
      errorHandler(err);
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};

export const useMutationFor2848Files = (
  uploadFileHandler: (event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateFilesQueryKey(dealId, '2848');

  return useMutation<
    void,
    unknown,
    ChangeEvent<HTMLInputElement>,
    { previous2848Documents: S3File[] | undefined }
  >(uploadFileHandler, {
    mutationKey,
    onMutate: (event) => {
      const fileName = event.target.files?.[0]?.name;
      const previous2848Documents: S3File[] | undefined = qc.getQueryData(
        generateFilesQueryKey(dealId, '2848'),
      );
      if (fileName) {
        let newDealDocument: S3File;
        if (previous2848Documents && previous2848Documents?.length > 0) {
          newDealDocument = {
            ...previous2848Documents?.[0],
          };
          newDealDocument.name = fileName;
        } else {
          newDealDocument = {
            name: fileName,
            size: 0,
            lastModified: '',
            contentType: '',
            url: '',
          };
        }

        qc.setQueryData(generateFilesQueryKey(dealId, '2848'), () => [
          newDealDocument,
        ]);
      }
      return { previous2848Documents };
    },
    onError: (err, _event, context) => {
      errorHandler(err);
      qc.setQueryData(
        generateFilesQueryKey(dealId, '2848'),
        context?.previous2848Documents,
      );
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};

export const useMutationFor8821Files = (
  uploadFileHandler: (event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateFilesQueryKey(dealId, '8821');

  return useMutation<
    void,
    unknown,
    ChangeEvent<HTMLInputElement>,
    { previous8821Documents: S3File[] | undefined }
  >(uploadFileHandler, {
    mutationKey,
    onMutate: (event) => {
      const fileName = event.target.files?.[0]?.name;
      const previous8821Documents: S3File[] | undefined = qc.getQueryData(
        generateFilesQueryKey(dealId, '8821'),
      );
      if (fileName) {
        let newDealDocument: S3File;
        if (previous8821Documents && previous8821Documents?.length > 0) {
          newDealDocument = {
            ...previous8821Documents?.[0],
          };
          newDealDocument.name = fileName;
        } else {
          newDealDocument = {
            name: fileName,
            size: 0,
            lastModified: '',
            contentType: '',
            url: '',
          };
        }

        qc.setQueryData(generateFilesQueryKey(dealId, '8821'), () => [
          newDealDocument,
        ]);
      }
      return { previous8821Documents };
    },
    onError: (err, _event, context) => {
      errorHandler(err);
      qc.setQueryData(
        generateFilesQueryKey(dealId, '8821'),
        context?.previous8821Documents,
      );
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};

export const useMutationForTaxEngagementFiles = (
  uploadFileHandler: (event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateFilesQueryKey(dealId, 'taxEngagement');

  return useMutation<
    void,
    unknown,
    ChangeEvent<HTMLInputElement>,
    { previousTaxEngagementFiles: S3File[] | undefined }
  >(uploadFileHandler, {
    mutationKey,
    onMutate: (event) => {
      const fileName = event.target.files?.[0]?.name;
      const previousTaxEngagementFiles: S3File[] | undefined = qc.getQueryData(
        generateFilesQueryKey(dealId, 'taxEngagement'),
      );
      if (fileName) {
        let newDealDocument: S3File;
        if (
          previousTaxEngagementFiles &&
          previousTaxEngagementFiles?.length > 0
        ) {
          newDealDocument = {
            ...previousTaxEngagementFiles?.[0],
          };
          newDealDocument.name = fileName;
        } else {
          newDealDocument = {
            name: fileName,
            size: 0,
            lastModified: '',
            contentType: '',
            url: '',
          };
        }

        qc.setQueryData(generateFilesQueryKey(dealId, 'taxEngagement'), () => [
          newDealDocument,
        ]);
      }
      return { previousTaxEngagementFiles };
    },
    onError: (err, _event, context) => {
      errorHandler(err);
      qc.setQueryData(
        generateFilesQueryKey(dealId, 'taxEngagement'),
        context?.previousTaxEngagementFiles,
      );
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};

export const useMutationForStatementOfFactsFiles = (
  uploadFileHandler: (event: ChangeEvent<HTMLInputElement>) => Promise<void>,
  dealId: string,
) => {
  const qc = useQueryClient();
  const mutationKey = generateFilesQueryKey(dealId, 'statementOfFacts');

  return useMutation<
    void,
    unknown,
    ChangeEvent<HTMLInputElement>,
    { previousStatementOfFactsFiles: S3File[] | undefined }
  >(uploadFileHandler, {
    mutationKey,
    onMutate: (event) => {
      const extension = event.target.files?.[0]?.name.split('.').pop();
      const fileName = `Statement of Facts - ${dealId}.${extension}`;
      const previousStatementOfFactsFiles: S3File[] | undefined =
        qc.getQueryData(generateFilesQueryKey(dealId, 'statementOfFacts'));
      if (fileName) {
        let newDealDocument: S3File;
        if (
          previousStatementOfFactsFiles &&
          previousStatementOfFactsFiles?.length > 0
        ) {
          newDealDocument = {
            ...previousStatementOfFactsFiles?.[0],
          };
          newDealDocument.name = fileName;
        } else {
          newDealDocument = {
            name: fileName,
            size: 0,
            lastModified: '',
            contentType: '',
            url: '',
          };
        }

        qc.setQueryData(
          generateFilesQueryKey(dealId, 'statementOfFacts'),
          () => [newDealDocument],
        );
      }
      return { previousStatementOfFactsFiles };
    },
    onError: (err, _event, context) => {
      errorHandler(err);
      qc.setQueryData(
        generateFilesQueryKey(dealId, 'statementOfFacts'),
        context?.previousStatementOfFactsFiles,
      );
    },
    onSettled: () => {
      invalidateQueryIfNotMutating(qc, mutationKey);
    },
  });
};
